// components/onboarding/IntegrationsOnboarding.js
import React, { useState, useEffect } from 'react';
import {
  Mail,
  MessageSquare,
  Star,
  MessageCircle,
  ArrowRight,
  Settings as SettingsIcon,
  CheckCircle,
  Zap,
  ShoppingBag,
  Briefcase,
  TrendingUp
} from 'lucide-react';
import IntegrationCard from './IntegrationCard';
import WidgetEmbedModal from '../modals/WidgetEmbedModal';
import widgetService from '../../services/widgetService';

const IntegrationsOnboarding = ({ onComplete, api }) => {
  const [connectingId, setConnectingId] = useState(null);
  const [integrationStatuses, setIntegrationStatuses] = useState({
    gmail: 'disconnected',
    facebook: 'disconnected',
    whatsapp: 'disconnected',
    widget: 'disconnected',
    trustpilot: 'disconnected',
    g2: 'disconnected',
    capterra: 'disconnected'
  });
  const [connectedCount, setConnectedCount] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [widgetApiKey, setWidgetApiKey] = useState('');

  // Trustpilot states
  const [trustpilotDomain, setTrustpilotDomain] = useState('');
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  
  // G2 states
  const [g2ProductSlug, setG2ProductSlug] = useState('');
  const [verifyingG2, setVerifyingG2] = useState(false);
  const [g2VerifyResult, setG2VerifyResult] = useState(null);
  
  // Capterra states
  const [capterraProductSlug, setCapterraProductSlug] = useState('');
  const [verifyingCapterra, setVerifyingCapterra] = useState(false);
  const [capterraVerifyResult, setCapterraVerifyResult] = useState(null);
  
  // G2 and Capterra status states
  const [g2Status, setG2Status] = useState(null);
  const [capterraStatus, setCapterraStatus] = useState(null);

  // Check connection statuses on mount
  useEffect(() => {
    checkAllStatuses();
  }, []);

  // Listen for OAuth callbacks
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'gmail-connected' && event.data.success) {
        setIntegrationStatuses(prev => ({ ...prev, gmail: 'connected' }));
        setConnectingId(null);
      }

      if (event.data?.type === 'facebook-connected' && event.data.success) {
        setIntegrationStatuses(prev => ({ ...prev, facebook: 'connected' }));
        setConnectingId(null);
      }

      if (event.data?.type === 'whatsapp-connected' && event.data.success) {
        setIntegrationStatuses(prev => ({ ...prev, whatsapp: 'connected' }));
        setConnectingId(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Update connected count
  useEffect(() => {
    const count = Object.values(integrationStatuses).filter(
      status => status === 'connected'
    ).length;
    setConnectedCount(count);
  }, [integrationStatuses]);

  const checkAllStatuses = async (showLoading = true) => {
    if (showLoading) {
      setIsCheckingStatus(true);
    }

    try {
      // Check Gmail
      const gmailStatus = await api.checkGmailStatus();
      if (gmailStatus?.connected) {
        setIntegrationStatuses(prev => ({ ...prev, gmail: 'connected' }));
      }

      // Check Facebook
      const fbStatus = await api.getFacebookStatus();
      if (fbStatus?.connected && fbStatus?.pages?.length > 0) {
        setIntegrationStatuses(prev => ({ ...prev, facebook: 'connected' }));
      }

      // Check WhatsApp
      const whatsappStatus = await api.getWhatsAppStatus();
      if (whatsappStatus?.connected && whatsappStatus?.phoneNumbers?.length > 0) {
        setIntegrationStatuses(prev => ({ ...prev, whatsapp: 'connected' }));
      }

      // Note: Widget is not checked here - it's always available and doesn't require "connection"

      // Check Trustpilot
      const trustpilotStatus = await api.getTrustpilotStatus();
      if (trustpilotStatus?.connected) {
        setIntegrationStatuses(prev => ({ ...prev, trustpilot: 'connected' }));
      }

      // Check G2
      const g2StatusResult = await api.getG2Status();
      if (g2StatusResult?.connected) {
        setIntegrationStatuses(prev => ({ ...prev, g2: 'connected' }));
      }
      setG2Status(g2StatusResult);

      // Check Capterra
      const capterraStatusResult = await api.getCapterraStatus();
      if (capterraStatusResult?.connected) {
        setIntegrationStatuses(prev => ({ ...prev, capterra: 'connected' }));
      }
      setCapterraStatus(capterraStatusResult);

      // Auto-complete if ANY real integration is connected (excluding widget)
      if (showLoading) {
        const hasAnyConnection = gmailStatus?.connected ||
                                (fbStatus?.connected && fbStatus?.pages?.length > 0) ||
                                (whatsappStatus?.connected && whatsappStatus?.phoneNumbers?.length > 0) ||
                                trustpilotStatus?.connected ||
                                g2StatusResult?.connected ||
                                capterraStatusResult?.connected;

        if (hasAnyConnection) {
          onComplete();
          return;
        }
      }

    } catch (error) {
      console.error('Failed to check integration statuses:', error);
    } finally {
      if (showLoading) {
        setIsCheckingStatus(false);
      }
    }
  };

  const handleConnect = async (integration) => {
    setConnectingId(integration.id);

    try {
      let response;

      switch (integration.id) {
        case 'gmail':
          response = await api.connectGmail();
          break;
        case 'facebook':
          response = await api.connectFacebook();
          break;
        case 'whatsapp':
          response = await api.connectWhatsApp();
          break;
        case 'widget':
          // Fetch widget settings (this will create config if it doesn't exist)
          try {
            const widgetSettings = await widgetService.getSettings();
            const widgetKey = widgetSettings?.apiKey || '';
            setWidgetApiKey(widgetKey);
            setShowWidgetModal(true);
            // Widget doesn't have "connected" status - it's just an embed code
          } catch (error) {
            console.error('Failed to fetch widget settings:', error);
          }
          setConnectingId(null);
          return;
        case 'trustpilot':
          setIntegrationStatuses(prev => ({
            ...prev,
            trustpilot: 'configuring'
          }));
          setConnectingId(null);
          return;
        case 'g2':
          setIntegrationStatuses(prev => ({
            ...prev,
            g2: 'configuring'
          }));
          setConnectingId(null);
          return;
        case 'capterra':
          setIntegrationStatuses(prev => ({
            ...prev,
            capterra: 'configuring'
          }));
          setConnectingId(null);
          return;
        default:
          return;
      }

      if (response?.authUrl) {
        const width = (integration.id === 'facebook' || integration.id === 'whatsapp') ? 700 : 500;
        const height = (integration.id === 'facebook' || integration.id === 'whatsapp') ? 800 : 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const authWindow = window.open(
          response.authUrl,
          `${integration.id}-oauth`,
          `width=${width},height=${height},left=${left},top=${top}`
        );

        const checkClosed = setInterval(() => {
          if (authWindow && authWindow.closed) {
            clearInterval(checkClosed);
            setConnectingId(null);
            setTimeout(() => checkAllStatuses(false), 1000);
          }
        }, 500);
      }
    } catch (error) {
      console.error(`Failed to connect ${integration.name}:`, error);
      setConnectingId(null);
    }
  };

  const handleVerifyTrustpilot = async () => {
    if (!trustpilotDomain.trim()) return;

    setVerifyingDomain(true);
    setVerifyResult(null);

    try {
      const result = await api.verifyTrustpilotDomain(trustpilotDomain);
      if (result.success) {
        setVerifyResult({
          success: true,
          domain: result.domain,
          companyInfo: result.companyInfo
        });
      }
    } catch (error) {
      setVerifyResult({
        success: false,
        error: error.message || 'Could not verify domain'
      });
    } finally {
      setVerifyingDomain(false);
    }
  };

  const handleSaveTrustpilot = async () => {
    if (!verifyResult?.success) return;

    setConnectingId('trustpilot');

    try {
      await api.updateWorkspaceTrustpilotDomain(verifyResult.domain);
      setIntegrationStatuses(prev => ({ ...prev, trustpilot: 'connected' }));
      setTrustpilotDomain('');
      setVerifyResult(null);
    } catch (error) {
      console.error('Failed to save Trustpilot domain:', error);
    } finally {
      setConnectingId(null);
    }
  };

  const handleCancelTrustpilot = () => {
    setIntegrationStatuses(prev => ({ ...prev, trustpilot: 'disconnected' }));
    setTrustpilotDomain('');
    setVerifyResult(null);
  };

  const handleVerifyG2 = async () => {
    if (!g2ProductSlug.trim()) return;

    setVerifyingG2(true);
    setG2VerifyResult(null);

    try {
      const result = await api.verifyG2Product(g2ProductSlug);
      if (result.success) {
        setG2VerifyResult({
          success: true,
          productSlug: result.productSlug,
          productInfo: result.productInfo
        });
      }
    } catch (error) {
      setG2VerifyResult({
        success: false,
        error: error.message || 'Could not verify product'
      });
    } finally {
      setVerifyingG2(false);
    }
  };

  const handleSaveG2 = async () => {
    if (!g2VerifyResult?.success) return;

    setConnectingId('g2');

    try {
      await api.updateWorkspaceG2Product(g2VerifyResult.productSlug);
      setIntegrationStatuses(prev => ({ ...prev, g2: 'connected' }));
      setG2ProductSlug('');
      setG2VerifyResult(null);
    } catch (error) {
      console.error('Failed to save G2 product:', error);
    } finally {
      setConnectingId(null);
    }
  };

  const handleCancelG2 = () => {
    setIntegrationStatuses(prev => ({ ...prev, g2: 'disconnected' }));
    setG2ProductSlug('');
    setG2VerifyResult(null);
  };

  const handleVerifyCapterra = async () => {
    if (!capterraProductSlug.trim()) return;

    setVerifyingCapterra(true);
    setCapterraVerifyResult(null);

    try {
      const result = await api.verifyCapterraProduct(capterraProductSlug);
      if (result.success) {
        setCapterraVerifyResult({
          success: true,
          productSlug: result.productSlug,
          productInfo: result.productInfo
        });
      }
    } catch (error) {
      setCapterraVerifyResult({
        success: false,
        error: error.message || 'Could not verify product'
      });
    } finally {
      setVerifyingCapterra(false);
    }
  };

  const handleSaveCapterra = async () => {
    if (!capterraVerifyResult?.success) return;

    setConnectingId('capterra');

    try {
      await api.updateWorkspaceCapterraProduct(capterraVerifyResult.productSlug);
      setIntegrationStatuses(prev => ({ ...prev, capterra: 'connected' }));
      setCapterraProductSlug('');
      setCapterraVerifyResult(null);
    } catch (error) {
      console.error('Failed to save Capterra product:', error);
    } finally {
      setConnectingId(null);
    }
  };

  const handleCancelCapterra = () => {
    setIntegrationStatuses(prev => ({ ...prev, capterra: 'disconnected' }));
    setCapterraProductSlug('');
    setCapterraVerifyResult(null);
  };

  // Define ticketing integrations
  const ticketingIntegrations = [
    {
      id: 'gmail',
      name: 'Gmail',
      description: 'Import customer tickets from Gmail automatically',
      icon: Mail,
      status: integrationStatuses.gmail,
      color: 'bg-red-500',
      implemented: true,
      category: 'Email'
    },
    {
      id: 'facebook',
      name: 'Facebook',
      description: 'Handle Facebook page messages and comments',
      icon: MessageSquare,
      status: integrationStatuses.facebook,
      color: 'bg-blue-600',
      implemented: true,
      category: 'Social Media'
    },
    {
      id: 'widget',
      name: 'Chat Widget',
      description: 'Add a chat widget to your website',
      icon: MessageCircle,
      status: integrationStatuses.widget,
      color: 'bg-purple-600',
      implemented: true,
      category: 'Website'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      description: 'Handle WhatsApp Business messages',
      icon: MessageCircle,
      status: integrationStatuses.whatsapp,
      color: 'bg-green-500',
      implemented: true,
      category: 'Social Media'
    }
  ];

  // Define review integrations
  const reviewIntegrations = [
    {
      id: 'trustpilot',
      name: 'Trustpilot',
      description: 'Monitor and respond to Trustpilot reviews',
      icon: Star,
      status: integrationStatuses.trustpilot,
      color: 'bg-teal-600',
      implemented: true,
      category: 'Reviews'
    },

    {
      id: 'capterra',
      name: 'Capterra',
      description: 'Handle software marketplace reviews',
      icon: TrendingUp,
      status: integrationStatuses.capterra,
      color: 'bg-blue-500',
      implemented: true,
      category: 'Reviews'
    },
    {
      id: 'reviewsio',
      name: 'Reviews.io',
      description: 'E-commerce review platform integration',
      icon: ShoppingBag,
      status: 'coming-soon',
      color: 'bg-emerald-500',
      implemented: false,
      category: 'Reviews'
    }
  ];

  // Show loading while checking status
  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-8">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Connect Your Channels
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Centralize customer support tickets and reviews from all your platforms
          </p>
        </div>

        {/* Progress Indicator */}
        {connectedCount > 0 && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800">
                {connectedCount} {connectedCount === 1 ? 'integration' : 'integrations'} connected
              </span>
            </div>
          </div>
        )}

        {/* Ticketing Channels Section */}
        <div className="mb-10">
          <div className="flex items-center mb-4">
            <MessageSquare className="w-5 h-5 text-purple-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Ticketing Channels</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ticketingIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConnect={handleConnect}
                isConnecting={connectingId}
              />
            ))}
          </div>
        </div>

        {/* Review Platforms Section */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Star className="w-5 h-5 text-amber-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Review Platforms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviewIntegrations.map((integration) => {
              // Special rendering for Trustpilot in configuration mode
              if (integration.id === 'trustpilot' && integrationStatuses.trustpilot === 'configuring') {
                return (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConnect={handleConnect}
                    isConnecting={connectingId}
                    configurationContent={
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type="text"
                            value={trustpilotDomain}
                            onChange={(e) => {
                              setTrustpilotDomain(e.target.value);
                              setVerifyResult(null);
                            }}
                            placeholder="yourcompany.com"
                            className="w-full px-3 py-2 border rounded-lg text-sm pr-8"
                          />
                          <div className="absolute right-2 top-2 group">
                            <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="absolute right-0 bottom-full mb-2 w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                              Enter your company domain. Example: "acmecompany.com"
                              <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                        
                        {verifyResult && (
                          <div className={`p-2 rounded text-xs ${
                            verifyResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            {verifyResult.success ? (
                              <div>✓ {verifyResult.companyInfo.companyName}</div>
                            ) : (
                              <div>{verifyResult.error}</div>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2">
                          {!verifyResult?.success ? (
                            <>
                              <button
                                onClick={handleVerifyTrustpilot}
                                disabled={verifyingDomain || !trustpilotDomain.trim()}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                              >
                                {verifyingDomain ? 'Verifying...' : 'Verify'}
                              </button>
                              <button
                                onClick={handleCancelTrustpilot}
                                className="px-3 py-2 border rounded-lg text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={handleSaveTrustpilot}
                              disabled={connectingId === 'trustpilot'}
                              className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {connectingId === 'trustpilot' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Connecting...
                                </>
                              ) : (
                                'Connect'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    }
                  />
                );
              }

              // Special rendering for G2 in configuration mode
              if (integration.id === 'g2' && integrationStatuses.g2 === 'configuring') {
                return (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConnect={handleConnect}
                    isConnecting={connectingId}
                    configurationContent={
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type="text"
                            value={g2ProductSlug}
                            onChange={(e) => {
                              setG2ProductSlug(e.target.value);
                              setG2VerifyResult(null);
                            }}
                            placeholder="https://www.g2.com/products/yourproduct/reviews"
                            className="w-full px-3 py-2 border rounded-lg text-sm pr-8"
                          />
                          <div className="absolute right-2 top-2 group">
                            <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="absolute right-0 bottom-full mb-2 w-72 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                              Paste your full G2 product URL. Example: "https://www.g2.com/products/yourproduct/reviews"
                              <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                        
                        {g2VerifyResult && (
                          <div className={`p-2 rounded text-xs ${
                            g2VerifyResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            {g2VerifyResult.success ? (
                              <div className="font-semibold">
                                ✓ {g2VerifyResult.productInfo.productName}
                                {g2VerifyResult.productInfo.averageRating && (
                                  <span className="text-gray-600 font-normal ml-2">
                                    {g2VerifyResult.productInfo.averageRating} ⭐ • {g2VerifyResult.productInfo.totalReviews?.toLocaleString() || 0} reviews
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div>{g2VerifyResult.error}</div>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2">
                          {!g2VerifyResult?.success ? (
                            <>
                              <button
                                onClick={handleVerifyG2}
                                disabled={verifyingG2 || !g2ProductSlug.trim()}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {verifyingG2 ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Verifying...
                                  </>
                                ) : (
                                  'Verify'
                                )}
                              </button>
                              <button
                                onClick={handleCancelG2}
                                className="px-3 py-2 border rounded-lg text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={handleSaveG2}
                              disabled={connectingId === 'g2'}
                              className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {connectingId === 'g2' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Connecting...
                                </>
                              ) : (
                                'Connect'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    }
                  />
                );
              }

              // Special rendering for Capterra in configuration mode
              if (integration.id === 'capterra' && integrationStatuses.capterra === 'configuring') {
                return (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConnect={handleConnect}
                    isConnecting={connectingId}
                    configurationContent={
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type="text"
                            value={capterraProductSlug}
                            onChange={(e) => {
                              setCapterraProductSlug(e.target.value);
                              setCapterraVerifyResult(null);
                            }}
                            placeholder="https://www.capterra.com/p/123456/YourProduct/"
                            className="w-full px-3 py-2 border rounded-lg text-sm pr-8"
                          />
                          <div className="absolute right-2 top-2 group">
                            <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="absolute right-0 bottom-full mb-2 w-72 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                              Paste your full Capterra product URL. Example: "https://www.capterra.com/p/123456/YourProduct/"
                              <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                        
                        {capterraVerifyResult && (
                          <div className={`p-2 rounded text-xs ${
                            capterraVerifyResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            {capterraVerifyResult.success ? (
                              <div className="font-semibold">
                                ✓ {capterraVerifyResult.productInfo.productName}
                                {capterraVerifyResult.productInfo.averageRating && (
                                  <span className="text-gray-600 font-normal ml-2">
                                    {capterraVerifyResult.productInfo.averageRating} ⭐ • {capterraVerifyResult.productInfo.totalReviews?.toLocaleString() || 0} reviews
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div>{capterraVerifyResult.error}</div>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2">
                          {!capterraVerifyResult?.success ? (
                            <>
                              <button
                                onClick={handleVerifyCapterra}
                                disabled={verifyingCapterra || !capterraProductSlug.trim()}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {verifyingCapterra ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Verifying...
                                  </>
                                ) : (
                                  'Verify'
                                )}
                              </button>
                              <button
                                onClick={handleCancelCapterra}
                                className="px-3 py-2 border rounded-lg text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={handleSaveCapterra}
                              disabled={connectingId === 'capterra'}
                              className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {connectingId === 'capterra' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Connecting...
                                </>
                              ) : (
                                'Connect'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    }
                  />
                );
              }

              return (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onConnect={handleConnect}
                  isConnecting={connectingId}
                />
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start space-x-3">
              <SettingsIcon className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  You can always add more integrations later
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Configure additional channels anytime in Settings → Integrations
                </p>
              </div>
            </div>

            <button
              onClick={onComplete}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors shadow-sm hover:shadow-md"
            >
              <span>Continue to Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Widget Embed Modal */}
      <WidgetEmbedModal
        isOpen={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        apiKey={widgetApiKey}
      />
    </div>
  );
};

export default IntegrationsOnboarding;